/**
 * Every user-visible text the manufacture import produces from its codes — preview lines, the
 * mapping step's issues, run failures and the downloadable report — built through an injected
 * `t` so this stays pure and the screen stays wiring.
 *
 * The code lists below are the translation contract: `manufactureImport.messages.test.js`
 * asserts each has a key in both locales, and the compile-time checks next to them fail
 * `tsc` when a code is added to a union without being listed here.
 */

import { type ManufactureImportField, type ManufactureMappingIssue, type ManufactureMappingIssueCode } from './files'
import { type ManufacturePreviewRow } from './preview'
import { type ManufactureImportFailure } from './runImport'
import { type ManufactureImportError, type ManufactureImportErrorCode, type RowRef } from './types'

/** i18next's `t`, as far as this module uses it. */
export type Translate = (key: string, options?: Record<string, unknown>) => string

export const MANUFACTURE_IMPORT_ERROR_CODES = [
  'MISSING_DATE',
  'INVALID_DATE',
  'MISSING_PRODUCT',
  'MISSING_QUANTITY',
  'INVALID_QUANTITY',
  'QUANTITY_NOT_STORABLE',
  'BATCH_TOO_LONG',
  'INGREDIENT_WITHOUT_NUMBER',
  'MISSING_PER_UNIT',
  'INVALID_PER_UNIT',
  'PRODUCT_NOT_FOUND',
  'INGREDIENT_NOT_FOUND',
  'AMBIGUOUS_PRODUCT',
  'AMBIGUOUS_INGREDIENT',
  'PRODUCT_NOT_COMPOSITE',
  'EMPTY_RECIPE',
  'INVALID_RECIPE',
  'GROUP_MISMATCH',
  'DUPLICATE_INGREDIENT',
  'FILE_UNREADABLE',
  'FILE_COLUMNS_MISSING',
  'FILE_EMPTY',
  'FILE_DUPLICATE_NAME',
] as const satisfies readonly ManufactureImportErrorCode[]

export const MANUFACTURE_MAPPING_ISSUE_CODES = [
  'MAPPING_REQUIRED_MISSING',
  'MAPPING_INGREDIENT_INCOMPLETE',
  'MAPPING_COLUMN_REUSED',
] as const satisfies readonly ManufactureMappingIssueCode[]

/** `buildManufactureInfo` refuses a run without a store with this message. */
export const STORE_REQUIRED = 'STORE_REQUIRED'

/** What GROUP_MISMATCH's `value` can name (comma-separated); `ingredient` = rows with and without one. */
export const GROUP_MISMATCH_FIELDS = ['date', 'product', 'quantity', 'batch', 'ingredient'] as const

export const MANUFACTURE_IMPORT_FIELD_NAMES = [
  'date',
  'product',
  'quantity',
  'batch',
  'manufactureNo',
  'ingredient',
  'ingredientPerUnit',
] as const satisfies readonly ManufactureImportField[]

// Compile-time exhaustiveness: a code added to a union but not to its list above fails tsc here.
type Unlisted<Union, List extends readonly unknown[]> = Exclude<Union, List[number]>
type NothingUnlisted<T extends never> = T
export type ManufactureImportCodeListsAreExhaustive = [
  NothingUnlisted<Unlisted<ManufactureImportErrorCode, typeof MANUFACTURE_IMPORT_ERROR_CODES>>,
  NothingUnlisted<Unlisted<ManufactureMappingIssueCode, typeof MANUFACTURE_MAPPING_ISSUE_CODES>>,
  NothingUnlisted<Unlisted<ManufactureImportField, typeof MANUFACTURE_IMPORT_FIELD_NAMES>>,
]

export const errorKey = (code: string): string => `manufactureImport.error.${code}`
export const fieldKey = (field: string): string => `manufactureImport.field.${field}`
export const mismatchFieldKey = (field: string): string => `manufactureImport.mismatchField.${field}`

/** `file : row`, or just `file` for an error that belongs to the whole file. */
export const errorLocation = (ref: ManufactureImportError['ref']): string =>
  ref.rowNumber === null ? ref.fileName : `${ref.fileName} : ${ref.rowNumber}`

/** `a.xlsx : 2, 3; b.xlsx : 5` — the rows of one manufacture, grouped by file in order. */
export const refsLocation = (refs: RowRef[]): string => {
  const byFile = new Map<string, number[]>()
  for (const { fileName, rowNumber } of refs) {
    const rows = byFile.get(fileName) ?? []
    rows.push(rowNumber)
    byFile.set(fileName, rows)
  }
  return [...byFile].map(([fileName, rows]) => `${fileName} : ${rows.join(', ')}`).join('; ')
}

/** The reason of one error, translated, with the offending value when there is one. */
export const errorReason = (error: ManufactureImportError, t: Translate): string => {
  if (error.code === 'GROUP_MISMATCH') {
    const fields = error.value
      .split(',')
      .map((field) => field.trim())
      .filter((field) => field !== '')
      .map((field) => t(mismatchFieldKey(field)))
      .join(', ')
    return t(errorKey(error.code), { fields })
  }
  const reason = t(errorKey(error.code))
  return error.value ? `${reason}: «${error.value}»` : reason
}

/** `file : row — reason` (file-level errors: `file — reason`). */
export const describeError = (error: ManufactureImportError, t: Translate): string =>
  `${errorLocation(error.ref)} — ${errorReason(error, t)}`

export const mappingIssueText = (issue: ManufactureMappingIssue, t: Translate): string =>
  t(errorKey(issue.code), { field: t(fieldKey(issue.field)) })

const INGREDIENT_NOT_FOUND_PREFIX = 'INGREDIENT_NOT_FOUND: '

/**
 * A run failure's message, translated. `runManufactureImport` formats the writer's refusal as
 * `INGREDIENT_NOT_FOUND: <product> / <ingredient>`; anything else is the raw error message.
 */
export const failureReason = (message: string, t: Translate): string => {
  if (message.startsWith(INGREDIENT_NOT_FOUND_PREFIX)) {
    const rest = message.slice(INGREDIENT_NOT_FOUND_PREFIX.length)
    const separator = rest.lastIndexOf(' / ')
    const product = separator === -1 ? rest : rest.slice(0, separator)
    const ingredient = separator === -1 ? '' : rest.slice(separator + ' / '.length)
    return t('manufactureImport.failure.INGREDIENT_NOT_FOUND', { product, ingredient })
  }
  if (message === STORE_REQUIRED) return t(errorKey(STORE_REQUIRED))
  return message
}

export interface BuildReportRowsOptions {
  errors: ManufactureImportError[]
  rows: ManufacturePreviewRow[]
  /** The finished run, or `null` for a report downloaded from the preview. */
  failures: ManufactureImportFailure[] | null
  t: Translate
}

export type ReportRow = Record<string, string | number>

/**
 * The downloadable report (`exportToExcel`): every rejected file/row first, then every prepared
 * manufacture with what happened to it — created, failed (why), skipped as a probable duplicate,
 * or, before the run, what will happen — and its warnings.
 */
export const buildReportRows = ({ errors, rows, failures, t }: BuildReportRowsOptions): ReportRow[] => {
  const column = {
    file: t('manufactureImport.report.file'),
    rows: t('manufactureImport.report.rows'),
    product: t('manufactureImport.report.product'),
    date: t('manufactureImport.report.date'),
    quantity: t('manufactureImport.report.quantity'),
    batch: t('manufactureImport.report.batch'),
    status: t('manufactureImport.report.status'),
    reason: t('manufactureImport.report.reason'),
  }
  const failureByKey = new Map((failures ?? []).map((failure) => [failure.key, failure]))

  const errorRows = errors.map(
    (error): ReportRow => ({
      [column.file]: error.ref.fileName,
      [column.rows]: error.ref.rowNumber ?? '',
      [column.product]: '',
      [column.date]: '',
      [column.quantity]: '',
      [column.batch]: '',
      [column.status]: t('manufactureImport.report.statusRejected'),
      [column.reason]: errorReason(error, t),
    })
  )

  const manufactureRows = rows.map((row): ReportRow => {
    const failure = failureByKey.get(row.key)
    let status: string
    if (!row.willImport) status = t('manufactureImport.report.statusSkippedDuplicate')
    else if (failures === null) status = t('manufactureImport.report.statusPlanned')
    else if (failure) status = t('manufactureImport.report.statusFailed')
    else status = t('manufactureImport.report.statusCreated')

    const notes = [
      ...(failure ? [failureReason(failure.message, t)] : []),
      ...(row.duplicate ? [t('manufactureImport.badge.duplicate')] : []),
      ...(row.twin ? [t('manufactureImport.badge.twin')] : []),
      ...(row.beforeRevision ? [t('manufactureImport.badge.beforeRevision')] : []),
    ]
    return {
      [column.file]: row.fileName,
      [column.rows]: row.refs.map((ref) => ref.rowNumber).join(', '),
      [column.product]: row.productName,
      [column.date]: row.date,
      [column.quantity]: row.quantity,
      [column.batch]: row.batchNumber ?? '',
      [column.status]: status,
      [column.reason]: notes.join('; '),
    }
  })

  return [...errorRows, ...manufactureRows]
}
